import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { setTemporaryCompletionBadge } from '../../../Redux/Dashboard/External/LearningHub/LearningHubCompBadAction';
import { fetchLearningHub } from '../../../Redux/Dashboard/External/LearningHub/LearningHubGetDataAction';
import { validateQuiz, resetQuizResult } from '../../../Redux/Dashboard/External/LearningHub/LearningHubQuizAction';
import { setTrainingSuccess } from '../../../Redux/Dashboard/External/LearningHub/TCTrainingStatusActions';

const Quiz = ({ questions, certName, id, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState(Array(questions.length).fill(''));
  const dispatch = useDispatch();
  const { result } = useSelector((state) => state.QuizValidate);
  const quizError = useSelector((state)=>state.QuizValidate?.error)


  // Function to handle the success case
  const handleQuizSuccess = () => {
    const badge = result?.['completion-badge'];
    dispatch(setTemporaryCompletionBadge(badge));

    if (certName === 'DSOM Tech Central Training') {
      dispatch(setTrainingSuccess(true));
    }

    dispatch(fetchLearningHub());

    Swal.fire({
      title: 'Quiz Passed',
      icon: 'success',
      html: `
        <table>
          <thead>
            <tr>
              <th><center>Passing Score</center></th>
              <th><center>Your Score</center></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><center>80</center></td>
              <td><center>
                <strong style="color: ${result?.score < result?.['passing-score'] ? 'red' : 'green'};">
                  ${result?.score}
                </strong>
              </center></td>
            </tr>
          </tbody>
          <p>${result?.result}</p>
        </table>
      `,
      confirmButtonText: 'Back',
    }).then(() => {
      if (onSuccess) {
        onSuccess();
        window.location.reload()
      }
      if (onClose) {
        onClose();
      }
      dispatch(resetQuizResult());
    });
  };

  // Function to handle the failure case
  const handleQuizFailure = () => {
    Swal.fire({
      title: `Quiz Failed`,
      icon: 'error',
      html: `
        <table>
          <thead>
            <tr>
              <th><center>Passing Score</center></th>
              <th><center>Your Score</center></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><center>80</center></td>
              <td><center>
                <strong style="color: ${result?.score < result?.['passing-score'] ? 'red' : 'green'};">
                  ${result?.score}
                </strong>
              </center></td>
            </tr>
          </tbody>
          <p>${result?.result}</p>
        </table>
      `,
      confirmButtonText: 'Retry Quiz',
    }).then((swalResult) => {
      if (swalResult.isConfirmed) {
        setCurrentStep(0);
        setAnswers(Array(questions.length).fill(''));
        dispatch(resetQuizResult());
      }
    });
  };

  // useEffect to handle quiz result changes
  useEffect(() => {
    if (result?.success && result?.score !== undefined) {
      if (result.score >= 80 && !result.learningHubFetched) {
        handleQuizSuccess();
        result.learningHubFetched = true; // Mark as fetched to prevent repeated fetches
      } else if (result.score < 80) {
        handleQuizFailure();
      }
    }
  }, [result, certName, onSuccess, onClose, questions.length]);


    // Profile Fetching Status
    useEffect(() => {
      if (quizError) {
        Swal.fire({
          title: 'Error',
          text: quizError,
          icon: 'error',
          confirmButtonText: 'Retry',
          customClass: {
            title: 'my-custom-title-of-pop-login',
            confirmButton: 'my-custom-button-of-pop',
          },
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.reload();
          }
        });
      }
    }, [quizError]);


  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleAnswerChange = (event) => {
    const { value, checked, type } = event.target;
    const { answerType } = questions[currentStep];

    if (answerType === 'multiple') {
      const newAnswers = [...answers];
      const currentAnswers = newAnswers[currentStep] ? newAnswers[currentStep].split(',').map(a => a.trim()) : [];
      if (checked) {
        if (!currentAnswers.includes(value)) {
          currentAnswers.push(value);
        }
      } else {
        const index = currentAnswers.indexOf(value);
        if (index > -1) {
          currentAnswers.splice(index, 1);
        }
      }
      newAnswers[currentStep] = currentAnswers.join(', ');
      setAnswers(newAnswers);
    } else {
      const newAnswers = [...answers];
      newAnswers[currentStep] = value;
      setAnswers(newAnswers);
    }
  };

  const handleSubmit = () => {
    const allAnswered = answers.every((answer) => answer !== '' && (answerType === 'multiple' ? answer.split(',').length > 0 : true));
  
    if (!allAnswered) {
      Swal.fire({
        title: 'Incomplete Quiz',
        text: 'You need to select an answer for all questions before submitting.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }
  
    const formattedAnswers = {};
    questions.forEach((question, index) => {
      const questionKey = Object.keys(question)[0].toLowerCase();
      const userAnswer = answers[index];
      
      // Sort user's answers alphabetically if it's a multiple-choice question
      if (question.answerType === 'multiple') {
        formattedAnswers[questionKey] = userAnswer.split(',').map(a => a.trim()).sort().join(', ');
      } else {
        formattedAnswers[questionKey] = userAnswer;
      }
    });
  
    console.log(formattedAnswers);
    dispatch(validateQuiz(certName, formattedAnswers, id));
  };
  

  const currentQuestion = questions[currentStep];
  const questionText = Object.values(currentQuestion)[0].replace(/\\n/g, '<br/>');
  const answerChoices = currentQuestion.answerChoice;
  const answerType = currentQuestion.answerType;

  return (
    <div className='quiz-background'>
      <p className='question-no'>Question {currentStep + 1}</p>
      <p className='Question' dangerouslySetInnerHTML={{ __html: questionText }}></p>

      <div className='mcq-question-sec'>
        {Object.entries(answerChoices).map(([key, value]) => (
          <div key={key}>
            {answerType === 'multiple' ? (
              <div className="custom-checkbox-container">
                <input
                  type="checkbox"
                  id={`check-option${key}`}
                  name={`question${currentStep}`}
                  value={key}
                  checked={answers[currentStep].split(',').map(a => a.trim()).includes(key)}
                  onChange={handleAnswerChange}
                />
                <p className='questi-check' htmlFor={`check-option${key}`}>{value}</p>
              </div>
            ) : (
              <div>
                <input
                  type="radio"
                  id={`option${key}`}
                  name={`question${currentStep}`}
                  value={key}
                  checked={answers[currentStep] === key}
                  onChange={handleAnswerChange}
                />
                <label htmlFor={`option${key}`}>{value}</label>
              </div>
            )}
          </div>
        ))}
      </div>

      <br />

      <div className='quiz-btn-flex'>
        {currentStep > 0 && (
          <button onClick={handlePrev} className='next-btn'>Previous</button>
        )}
        {currentStep < questions.length - 1 && (
          <button onClick={handleNext} className='next-btn'>Next</button>
        )}
        {currentStep === questions.length - 1 && (
          <button onClick={handleSubmit} className='submit-btn'>Submit</button>
        )}
      </div>
    </div>
  );
};

export default Quiz;
